import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
    Camera, Upload, Loader2, CheckCircle, AlertTriangle, XCircle, 
    ShoppingCart, RotateCcw, Droplets, FlaskConical, Image as ImageIcon,
    Info, MapPin, Phone
} from 'lucide-react';
import { apiRequest, formatPrice, API_URL } from '../lib/utils';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import AnalysisHistoryChart from '../components/AnalysisHistoryChart';

export default function PhotoAnalysisPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { addToCart } = useCart();
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [poolVolume, setPoolVolume] = useState('50');
    const [treatmentType, setTreatmentType] = useState('chlore');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error('Veuillez sélectionner une image');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            toast.error('Image trop volumineuse (max 10MB)');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            setImagePreview(e.target.result);
            setImage(e.target.result);
            setResult(null);
        };
        reader.readAsDataURL(file);
    };

    const handleAnalyze = async () => {
        if (!image) {
            toast.error('Veuillez d\'abord prendre ou sélectionner une photo');
            return;
        }

        setLoading(true);
        try {
            const data = await apiRequest('/analysis/photo', {
                method: 'POST',
                body: JSON.stringify({
                    image_base64: image,
                    pool_volume: parseFloat(poolVolume),
                    treatment_type: treatmentType
                })
            });
            setResult(data);
            
            if (data.status === 'success') {
                toast.success('Analyse terminée !');
            } else if (data.status === 'error') {
                toast.error(data.message || 'Erreur lors de l\'analyse');
            }
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAddToCart = async (productId) => {
        if (!user) {
            toast.error('Connectez-vous pour ajouter au panier');
            navigate('/auth');
            return;
        }
        try {
            await addToCart(productId);
            toast.success('Produit ajouté au panier');
        } catch (error) {
            toast.error(error.message);
        }
    };

    const resetAnalysis = () => {
        setImage(null);
        setImagePreview(null);
        setResult(null);
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'ok':
            case 'good':
                return <CheckCircle className="h-5 w-5 text-emerald-500" />;
            case 'warning':
            case 'low':
            case 'high':
                return <AlertTriangle className="h-5 w-5 text-amber-500" />;
            case 'critical':
                return <XCircle className="h-5 w-5 text-red-500" />;
            default:
                return <Droplets className="h-5 w-5 text-primary" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'ok':
            case 'good':
                return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30';
            case 'warning':
            case 'low':
            case 'high':
                return 'bg-amber-500/10 text-amber-500 border-amber-500/30';
            case 'critical':
                return 'bg-red-500/10 text-red-500 border-red-500/30';
            default:
                return 'bg-muted text-muted-foreground';
        }
    };

    return (
        <div className="min-h-screen pt-20 pb-12" data-testid="photo-analysis-page">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <Camera className="h-10 w-10 text-primary" />
                        <h1 className="font-heading text-3xl sm:text-4xl font-bold">Analyse Photo</h1>
                    </div>
                    <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                        Prenez en photo votre bandelette AquaChek et recevez une analyse instantanée avec les produits CTX Pro recommandés.
                    </p>
                </div>

                {/* Important Disclaimer Banner */}
                <div className="mb-8 p-4 sm:p-6 rounded-2xl bg-amber-500/10 border-2 border-amber-500/30" data-testid="photo-disclaimer">
                    <div className="flex items-start gap-4">
                        <div className="h-10 w-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                            <AlertTriangle className="h-5 w-5 text-amber-500" />
                        </div>
                        <div className="flex-1">
                            <h3 className="font-semibold text-amber-600 dark:text-amber-400 mb-2">
                                Analyse indicative uniquement
                            </h3>
                            <p className="text-sm text-muted-foreground mb-3">
                                L&apos;analyse par photo de bandelette est une <strong>estimation</strong> basée sur la reconnaissance d&apos;image. 
                                Pour une analyse <strong>précise et fiable</strong> de votre eau, nous vous recommandons de venir en magasin 
                                avec un échantillon d&apos;eau pour un test professionnel gratuit.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <div className="flex items-center gap-2 text-sm">
                                    <MapPin className="h-4 w-4 text-primary" />
                                    <span>5 Place de la République, 83780 Flayosc</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                    <Phone className="h-4 w-4 text-primary" />
                                    <span>06 51 96 14 74</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid lg:grid-cols-2 gap-8">
                    {/* Left: Photo capture */}
                    <Card className="glass rounded-3xl" data-testid="photo-capture">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <FlaskConical className="h-5 w-5 text-primary" />
                                Votre bandelette
                            </CardTitle>
                            <CardDescription>
                                Photographiez votre bandelette AquaChek après l'avoir trempée
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Image preview or placeholder */}
                            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-muted border-2 border-dashed border-border flex items-center justify-center">
                                {imagePreview ? (
                                    <>
                                        <img 
                                            src={imagePreview} 
                                            alt="Bandelette" 
                                            className="w-full h-full object-contain"
                                        />
                                        <Button
                                            variant="destructive"
                                            size="icon"
                                            className="absolute top-3 right-3 rounded-full"
                                            onClick={resetAnalysis}
                                        >
                                            <RotateCcw className="h-4 w-4" />
                                        </Button>
                                    </>
                                ) : (
                                    <div className="text-center p-8">
                                        <ImageIcon className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
                                        <p className="text-muted-foreground">
                                            Prenez ou sélectionnez une photo de votre bandelette
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Capture buttons */}
                            <div className="grid grid-cols-2 gap-4">
                                <input
                                    ref={cameraInputRef}
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />
                                <Button
                                    variant="secondary"
                                    className="rounded-xl h-14"
                                    onClick={() => cameraInputRef.current?.click()}
                                    data-testid="camera-btn"
                                >
                                    <Camera className="h-5 w-5 mr-2" />
                                    Prendre photo
                                </Button>

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />
                                <Button
                                    variant="secondary"
                                    className="rounded-xl h-14"
                                    onClick={() => fileInputRef.current?.click()}
                                    data-testid="upload-btn"
                                >
                                    <Upload className="h-5 w-5 mr-2" />
                                    Galerie
                                </Button>
                            </div>

                            {/* Pool info */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Volume du bassin (m³)</Label>
                                    <Input
                                        type="number"
                                        value={poolVolume}
                                        onChange={(e) => setPoolVolume(e.target.value)}
                                        className="h-12 rounded-xl"
                                        data-testid="pool-volume"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Type de traitement</Label>
                                    <Select value={treatmentType} onValueChange={setTreatmentType}>
                                        <SelectTrigger className="h-12 rounded-xl" data-testid="treatment-type">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="chlore">Chlore</SelectItem>
                                            <SelectItem value="brome">Brome</SelectItem>
                                            <SelectItem value="sel">Sel (électrolyse)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Analyze button */}
                            <Button
                                className="w-full rounded-full h-14 font-semibold text-lg"
                                onClick={handleAnalyze}
                                disabled={!image || loading}
                                data-testid="analyze-photo-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                        Analyse en cours...
                                    </>
                                ) : (
                                    <>
                                        <FlaskConical className="mr-2 h-5 w-5" />
                                        Analyser ma bandelette
                                    </>
                                )}
                            </Button>

                            {/* Tips */}
                            <div className="bg-primary/5 rounded-xl p-4">
                                <h4 className="font-semibold mb-2 text-sm">Conseils pour une bonne photo :</h4>
                                <ul className="text-xs text-muted-foreground space-y-1">
                                    <li>• Bon éclairage naturel</li>
                                    <li>• Bandelette à plat sur fond blanc</li>
                                    <li>• Attendez le temps indiqué avant la photo</li>
                                    <li>• Évitez les reflets et les ombres</li>
                                </ul>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Right: Results */}
                    <div className="space-y-6">
                        {result ? (
                            <>
                                {result.status === 'error' ? (
                                    <Card className="glass rounded-3xl border-2 border-red-500/30">
                                        <CardContent className="py-12 text-center">
                                            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                                            <h3 className="font-heading text-xl font-semibold mb-2">
                                                Impossible d'analyser
                                            </h3>
                                            <p className="text-muted-foreground mb-4">
                                                {result.message || 'La photo n\'est pas assez claire pour l\'analyse.'}
                                            </p>
                                            <Button onClick={resetAnalysis} className="rounded-full">
                                                Reprendre la photo
                                            </Button>
                                        </CardContent>
                                    </Card>
                                ) : (
                                    <>
                                        {/* Readings */}
                                        {result.readings && (
                                            <Card className={`glass rounded-3xl border-2 ${getStatusColor(result.overall_status)}`} data-testid="analysis-results">
                                                <CardHeader>
                                                    <CardTitle className="flex items-center gap-2">
                                                        {getStatusIcon(result.overall_status)}
                                                        Diagnostic AquaChek
                                                    </CardTitle>
                                                </CardHeader>
                                                <CardContent className="space-y-4">
                                                    <div className="grid grid-cols-2 gap-3">
                                                        {Object.entries(result.readings).map(([key, data]) => (
                                                            <div 
                                                                key={key} 
                                                                className={`p-3 rounded-xl border ${getStatusColor(data.status)}`}
                                                            >
                                                                <div className="flex items-center justify-between mb-1">
                                                                    <span className="text-sm font-medium capitalize">{key}</span>
                                                                    {getStatusIcon(data.status)}
                                                                </div>
                                                                <p className="font-mono font-bold">{data.value}</p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    
                                                    {result.diagnostic && (
                                                        <div className="p-4 bg-muted/50 rounded-xl">
                                                            <p className="text-sm">{result.diagnostic}</p>
                                                        </div>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        )}

                                        {/* Treatments */}
                                        {result.treatments && result.treatments.length > 0 && (
                                            <Card className="glass rounded-3xl" data-testid="treatments-card">
                                                <CardHeader>
                                                    <CardTitle className="flex items-center gap-2">
                                                        <ShoppingCart className="h-5 w-5 text-primary" />
                                                        Traitement CTX Pro recommandé
                                                    </CardTitle>
                                                    <CardDescription>
                                                        Pour votre bassin de {poolVolume} m³
                                                    </CardDescription>
                                                </CardHeader>
                                                <CardContent className="space-y-4">
                                                    {result.treatments.map((treatment, index) => (
                                                        <div 
                                                            key={index}
                                                            className="p-4 rounded-2xl border border-border bg-muted/30"
                                                            data-testid={`treatment-${index}`}
                                                        >
                                                            <div className="flex items-start justify-between gap-4">
                                                                <div className="flex-1">
                                                                    <h4 className="font-semibold">{treatment.product_name}</h4>
                                                                    <p className="text-sm font-mono text-primary font-bold mt-1">
                                                                        Dosage : {treatment.quantity}
                                                                    </p>
                                                                    <p className="text-sm text-muted-foreground mt-2">
                                                                        {treatment.instruction}
                                                                    </p>
                                                                </div>
                                                                {treatment.product_id && (
                                                                    <Button
                                                                        size="sm"
                                                                        className="rounded-full"
                                                                        onClick={() => handleAddToCart(treatment.product_id)}
                                                                    >
                                                                        <ShoppingCart className="h-4 w-4 mr-1" />
                                                                        Ajouter
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}

                                                    <Button 
                                                        className="w-full rounded-full"
                                                        onClick={() => navigate('/cart')}
                                                    >
                                                        <ShoppingCart className="h-4 w-4 mr-2" />
                                                        Voir mon panier CTX
                                                    </Button>
                                                </CardContent>
                                            </Card>
                                        )}

                                        {/* If good status with no treatments */}
                                        {result.overall_status === 'good' && (!result.treatments || result.treatments.length === 0) && (
                                            <Card className="glass rounded-3xl border-2 border-emerald-500/30">
                                                <CardContent className="py-8 text-center">
                                                    <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
                                                    <h3 className="font-heading text-xl font-semibold mb-2">
                                                        Eau parfaitement équilibrée !
                                                    </h3>
                                                    <p className="text-muted-foreground">
                                                        Tous vos paramètres sont dans les zones idéales. Continuez ainsi !
                                                    </p>
                                                </CardContent>
                                            </Card>
                                        )}

                                        {/* Professional analysis reminder */}
                                        <div className="p-4 rounded-xl bg-primary/5 border border-primary/20" data-testid="pro-analysis-reminder">
                                            <div className="flex items-start gap-3">
                                                <Info className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                                <div>
                                                    <p className="text-sm font-medium mb-1">Vous souhaitez une analyse plus précise ?</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        Venez nous voir en magasin à Flayosc avec un échantillon d&apos;eau. 
                                                        Nous effectuerons une analyse professionnelle gratuite avec notre testeur SpinTouch LaMotte.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Raw response fallback */}
                                        {result.raw_response && !result.readings && (
                                            <Card className="glass rounded-3xl">
                                                <CardHeader>
                                                    <CardTitle>Résultat de l'analyse</CardTitle>
                                                </CardHeader>
                                                <CardContent>
                                                    <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                                                        {result.raw_response}
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        )}

                                        {/* History chart (for logged-in users only) */}
                                        {user && (
                                            <AnalysisHistoryChart compact />
                                        )}
                                    </>
                                )}
                            </>
                        ) : (
                            <Card className="glass rounded-3xl">
                                <CardContent className="py-16 text-center">
                                    <Camera className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
                                    <h3 className="font-heading text-xl font-semibold mb-2 text-muted-foreground">
                                        Prenez une photo
                                    </h3>
                                    <p className="text-muted-foreground">
                                        Photographiez votre bandelette AquaChek pour recevoir une analyse détaillée.
                                    </p>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
